import {
  EntityV2Store,
  ManageCRMStore,
  aguserStore,
  configStore,
  loginStore,
  routerStore,
} from "../../state/CommonState";
import AguserApi from "../../state/AguserApi";
import { CrmColumn } from "../../components/ag-table/columns";
import { loader } from "../../util/loader";
import { Sorting } from "../../components/ag-table/ag-sorting";
import React, { useState, useEffect } from "react";
import { useTable, usePagination, useSortBy } from "react-table";
import { QueryClient, QueryClientProvider } from "react-query";
import ReactTablePagination from "../../components/ag-table/ReactTablePagination";
import TableFilter from "../../components/ag-table/TableFilter";
import { useMobile } from "../../hooks/usemobile";

import {
  reducer,
  PAGE_CHANGED,
  PAGE_SIZE_CHANGED,
  PAGE_SORT_CHANGED,
  PAGE_FILTER_CHANGED,
  TOTAL_COUNT_CHANGED,
  PAYLOAD_CHANGED,
} from "../../components/ag-table/ag-reducer";
import ApiAgTable from "../../components/ag-table/api_ag-table";
import { useSnapshot } from "valtio";
import Utils from "../../util/Utils";

const queryClient = new QueryClient();
const initialState = {
  queryPageIndex: 0,
  queryPageSize: 20,
  totalCount: 0,
  queryPageFilter: "",
  queryPageSortBy: [],
  queryPayload: {},
};

// --- Regex Helper Function ---
// Extracts the name before the parentheses, or the username from an email
export const extractName = (attendeeStr) => {
  if (!attendeeStr) return "";
  if (typeof attendeeStr === "object") {
    return attendeeStr.name || attendeeStr.fullName || attendeeStr.email || "";
  }

  // If attendee is a raw email, check if aguserStore has full name
  if (
    typeof attendeeStr === "string" &&
    attendeeStr.includes("@") &&
    !attendeeStr.includes("(") &&
    aguserStore?.list?.length > 0
  ) {
    const resolvedName = aguserStore.getName4email(attendeeStr);
    if (resolvedName) {
      attendeeStr = resolvedName;
    }
  }

  // 1. Check for "Name (email)" format
  const nameMatch = attendeeStr.match(/^(.*?)\s*\(/);
  if (nameMatch && nameMatch[1]) {
    return nameMatch[1].trim();
  }

  // 2. Fallback for raw emails (extracts everything before the '@')
  const emailMatch = attendeeStr.match(/^([^@]+)@/);
  if (emailMatch && emailMatch[1]) {
    return emailMatch[1].trim();
  }

  // 3. Return original string if no match
  return attendeeStr;
};

const meetingTypes = ["ALL", "In-Person", "Video Call", "Phone Call"];

const outcomes = ["ALL", "Positive", "Neutral", "Needs Follow-up", "Pending"];

// --- Outcome Badge (Tailwind CSS) ---
const getOutcomeBadge = (outcome) => {
  const status = outcome?.toLowerCase() || "pending";

  switch (status) {
    case "positive":
      return (
        <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium text-green-700 bg-green-100 rounded-full">
          <span className="w-1.5 h-1.5 mr-1.5 bg-green-500 rounded-full"></span>
          Positive
        </span>
      );
    case "needs follow-up":
      return (
        <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium text-orange-700 bg-orange-100 rounded-full">
          <span className="w-1.5 h-1.5 mr-1.5 bg-orange-500 rounded-full"></span>
          Needs Follow-up
        </span>
      );
    case "neutral":
      return (
        <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium text-blue-700 bg-blue-100 rounded-full">
          <span className="w-1.5 h-1.5 mr-1.5 bg-blue-500 rounded-full"></span>
          Neutral
        </span>
      );
    case "pending":
    default:
      return (
        <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-full">
          <span className="w-1.5 h-1.5 mr-1.5 bg-gray-500 rounded-full"></span>
          {outcome || "Pending"}
        </span>
      );
  }
};

// --- Meeting Type Icon (Pure Inline SVGs + Tailwind CSS) ---
const getIconForMeetingType = (meetingType) => {
  if (meetingType === "In-Person") {
    return (
      <svg className="w-4 h-4 text-gray-700 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
    );
  } else if (meetingType === "Video Call") {
    return (
      <svg className="w-4 h-4 text-gray-700 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
      </svg>
    );
  } else if (meetingType === "Phone Call") {
    return (
      <svg className="w-4 h-4 text-gray-700 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
      </svg>
    );
  }
  return null;
};

// --- Meeting Card for Mobile View (Tailwind CSS) ---
const MeetingCard = ({ data, onEdit, onView }) => {
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const externalAttendeesList = (data.externalAttendees || [])
    .map(extractName)
    .filter(Boolean)
    .join(", ");

  const internalAttendeesList = (data.internalAttendees || [])
    .map(extractName)
    .filter(Boolean)
    .join(", ");

  return (
    <div
      onClick={() => onView && onView(data)}
      className="w-full p-4 bg-white border border-gray-100 shadow-sm rounded-xl font-sans flex flex-col cursor-pointer active:scale-[0.99] transition-all hover:border-gray-300"
    >
      {/* Header: ID/Date and Outcome Badge */}
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm text-gray-500 font-medium">
          #{formatDate(data.dateTime)}
        </div>
        {data.outcome && data.outcome.length > 0 && getOutcomeBadge(data.outcome)}
      </div>

      {/* Meeting Type & Edit Button */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-gray-800">
          {getIconForMeetingType(data.type)}
          <span>{data.type || "Meeting"}</span>
        </div>
      </div>

      {/* Attendees Columns */}
      <div className="flex justify-between mb-3 text-xs">
        <div className="w-1/2 pr-2">
          <p className="mb-0.5 text-xs text-gray-500 font-normal">Attendees</p>
          <p className="text-xs sm:text-sm font-medium text-gray-800 truncate" title={externalAttendeesList}>
            {externalAttendeesList || "None"}
          </p>
        </div>
        <div className="w-1/2 pl-2">
          <p className="mb-0.5 text-xs text-gray-500 font-normal">Internal Attendees</p>
          <p className="text-xs sm:text-sm font-medium text-gray-800 truncate" title={internalAttendeesList}>
            {internalAttendeesList || "None"}
          </p>
        </div>
      </div>

      {/* Meeting Notes Box */}
      <div className="p-3 mb-3 rounded-lg bg-[#f9fafb] shadow">
        <p className="mb-1 text-xs text-gray-500 font-normal">Meeting Notes</p>
        <p className="text-xs sm:text-sm text-gray-700 line-clamp-2 leading-relaxed">
          {data.meetingNotes || "No notes available."}
        </p>
      </div>

      {/* Footer: Follow-up Date */}
      <div className="flex items-center justify-between text-xs sm:text-sm text-gray-500 pt-1 border-t border-gray-50">
        <div className="flex items-center">
          <svg className="w-4 h-4 mr-1 text-[#7c86ff]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
          </svg>
          <span>Next follow-up</span>
        </div>
        <div className="font-medium text-gray-800">
          {data.followUpDate ? formatDate(data.followUpDate) : "Not scheduled"}
        </div>
      </div>
    </div>
  );
};

// --- Card Skeleton for Loading State (Tailwind CSS) ---
export function CardSkeleton() {
  return (
    <div className="w-full border border-gray-100 rounded-xl p-5 bg-white space-y-4 shadow-sm animate-pulse font-sans">
      {/* Header Row: Date & Badge */}
      <div className="flex justify-between items-center">
        <div className="h-4 w-24 bg-gray-200 rounded"></div>
        <div className="h-5 w-16 bg-gray-200 rounded-full"></div>
      </div>

      {/* Call Type */}
      <div className="flex items-center gap-2">
        <div className="h-4 w-4 bg-gray-200 rounded"></div>
        <div className="h-4 w-20 bg-gray-200 rounded"></div>
      </div>

      {/* Attendees Section */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <div className="h-3 w-16 bg-gray-200 rounded"></div>
          <div className="h-4 w-24 bg-gray-200 rounded"></div>
        </div>
        <div className="space-y-1.5">
          <div className="h-3 w-24 bg-gray-200 rounded"></div>
          <div className="h-4 w-28 bg-gray-200 rounded"></div>
        </div>
      </div>

      {/* Meeting Notes Box */}
      <div className="border border-gray-50 bg-gray-50 rounded-lg p-3 space-y-2">
        <div className="h-3 w-20 bg-gray-200 rounded"></div>
        <div className="h-3.5 w-full bg-gray-200 rounded"></div>
        <div className="h-3.5 w-3/4 bg-gray-200 rounded"></div>
      </div>

      {/* Footer Row: Follow-up */}
      <div className="flex justify-between items-center pt-1">
        <div className="flex items-center gap-1.5">
          <div className="h-4 w-4 bg-gray-200 rounded"></div>
          <div className="h-3.5 w-24 bg-gray-200 rounded"></div>
        </div>
        <div className="h-3.5 w-20 bg-gray-200 rounded"></div>
      </div>
    </div>
  );
}

const MeetingDetails = () => {
  useSnapshot(ManageCRMStore);
  const Entity = EntityV2Store.EntityRec;
  useSnapshot(configStore);
  useSnapshot(aguserStore);
  useSnapshot(loginStore);
  const isMobile = useMobile();

  const [keyword, setKeyword] = useState("");
  const [selectedFilter, SetSelectedFilter] = useState("ALL");
  const [selectedBranch, SetSelectedBranch] = useState("ALL");
  const [selected1Filter, SetSelected1Filter] = useState("ALL");
  const [data1, setData1] = useState({ results: [] });
  const [hasMounted, setHasMounted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const onClickFilterCallback = (filter) => {
    setKeyword(filter);
  };
  let columns = CrmColumn;
  const [
    {
      queryPageIndex,
      queryPageSize,
      totalCount,
      queryPageFilter,
      queryPageSortBy,
      queryPayload,
    },
    dispatch,
  ] = React.useReducer(reducer, initialState);

  // Auto-fetch internal users for predefined attendees in mobile and web view
  useEffect(() => {
    if (!aguserStore.list || aguserStore.list.length === 0) {
      AguserApi.getList();
    }
  }, []);

  // Predefined internal attendee formatted as "FirstName LastName (email)"
  const getPredefinedInternalAttendee = () => {
    const userEmail = loginStore.email || loginStore.userRec?.email;
    if (userEmail && aguserStore.list && aguserStore.list.length > 0) {
      const match = aguserStore.getName4email(userEmail);
      if (match) return match;
    }
    const user = loginStore.userRec;
    if (user?.firstName) {
      const fullName = `${user.firstName} ${user.lastName || ""}`.trim();
      const email = user.email || loginStore.email;
      return email ? `${fullName} (${email})` : fullName;
    }
    return userEmail || "";
  };

  useEffect(() => {
    if (hasMounted) {
      loader.show();
      setIsLoading(true);
      ApiAgTable.getcrmlist(
        queryPageIndex,
        queryPageSize,
        queryPageFilter,
        queryPageSortBy,
        queryPayload,
      )
        .then((res) => {
          res
            .clone()
            .json()
            .then(
              (res) => {
                if (
                  JSON.stringify(data1.results) !== JSON.stringify(res.results)
                ) {
                  if (
                    JSON.stringify(data1) !== JSON.stringify(res) &&
                    res.results &&
                    res.results.length > 0
                  ) {
                    setData1(res);
                  } else {
                    setData1({ count: 0, results: [] });
                  }
                }
                loader.hide();
                setIsLoading(false);
              },
              (err) => {
                console.log(err);
                loader.hide();
                setIsLoading(false);
              },
            );
        })
        .catch((err) => {
          console.log(err);
          loader.hide();
          setIsLoading(false);
        });
    } else {
      setHasMounted(true);
    }
  }, [
    queryPayload,
    queryPageIndex,
    queryPageSize,
    queryPageFilter,
    queryPageSortBy,
    hasMounted,
  ]);

  const totalPageCount = Math.ceil(totalCount / queryPageSize);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    page,
    pageCount,
    pageOptions,
    gotoPage,
    previousPage,
    canPreviousPage,
    nextPage,
    canNextPage,
    setPageSize,
    state: { pageIndex, pageSize, sortBy },
  } = useTable(
    {
      columns,
      data: data1?.results || [],
      initialState: {
        pageIndex: queryPageIndex,
        pageSize: queryPageSize,
        sortBy: queryPageSortBy,
      },
      manualPagination: true,
      pageCount: data1 ? totalPageCount : null,
      autoResetSortBy: false,
      autoResetExpanded: false,
      autoResetPage: false,
    },
    useSortBy,
    usePagination,
  );

  const manualPageSize = [];

  useEffect(() => {
    dispatch({ type: PAGE_CHANGED, payload: pageIndex });
  }, [pageIndex, gotoPage]);

  useEffect(() => {
    dispatch({ type: PAGE_SIZE_CHANGED, payload: pageSize });
    gotoPage(0);
  }, [pageSize, gotoPage]);

  useEffect(() => {
    dispatch({ type: PAGE_SORT_CHANGED, payload: sortBy });
    gotoPage(0);
  }, [sortBy, gotoPage]);

  useEffect(() => {
    if (keyword.length === 0) {
      dispatch({
        type: PAYLOAD_CHANGED,
        payload: {
          search: keyword,
          branch: selectedBranch,
          filter: selectedFilter,
          filter1: selected1Filter,
          entityId: Entity.entityId,
        },
      });
      gotoPage(0);
    }
  }, [keyword]);

  useEffect(() => {
    dispatch({
      type: PAYLOAD_CHANGED,
      payload: {
        search: keyword,
        branch: selectedBranch,
        filter: selectedFilter,
        filter1: selected1Filter,
        entityId: Entity.entityId,
      },
    });
    gotoPage(0);
  }, [selectedBranch, selectedFilter, selected1Filter]);

  const onClickSearch = () => {
    if (keyword.length > 0) {
      dispatch({
        type: PAYLOAD_CHANGED,
        payload: {
          search: keyword,
          branch: selectedBranch,
          filter: selectedFilter,
          filter1: selected1Filter,
          entityId: Entity.entityId,
        },
      });
      gotoPage(0);
    } else {
      configStore.setModalMessage("Please Provide Valid Input");
    }
  };

  React.useEffect(() => {
    if (data1?.count) {
      dispatch({
        type: TOTAL_COUNT_CHANGED,
        payload: data1.count,
      });
    }
  }, [data1?.count]);

  const handleAddButton = () => {
    const defaultAttendee = getPredefinedInternalAttendee();
    ManageCRMStore.manageCRMRec.newset(
      defaultAttendee ? [defaultAttendee] : []
    );
    routerStore.managecrmCurrentViewNew = "new";
  };

  const handleEditMeeting = (item) => {
    ManageCRMStore.manageCRMRec.set(item);
    routerStore.managecrmCurrentViewNew = "edit";
  };

  const handleViewMeeting = (item) => {
    ManageCRMStore.manageCRMRec.set(item);
    routerStore.managecrmCurrentViewNew = "view";
  };

  const getStatusColor = (label) => {
    switch (label) {
      case "Positive":
        return "#4CAF50"; // Green
      case "Neutral":
        return "#2196F3"; // Blue
      case "Needs Follow-up":
        return "#e100ff"; // Purple
      case "Pending":
        return "#F44336"; // Red
      default:
        return ""; // Grey
    }
  };

  const getFollowUpColor = (dateStr) => {
    if (!dateStr) return "";

    const today = new Date();
    const followDate = new Date(dateStr);

    today.setHours(0, 0, 0, 0);
    followDate.setHours(0, 0, 0, 0);

    const diffDays = (followDate - today) / (1000 * 60 * 60 * 24);

    if (diffDays < 0) return "#F44336"; // Overdue (Red)
    if (diffDays <= 3) return "#e100ff"; // Within 3 days (Purple)

    return "";
  };

  return (
    <>
      {isMobile ? (
        /* Mobile Screen UI using Tailwind CSS */
        <div className="flex flex-col gap-3 w-full font-sans">
          {/* Search Bar + Add Meeting Button */}
          <div className="flex flex-row gap-2 w-full">
            <div className="relative flex-1">
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="Search..."
                className="w-full h-[41.6px] px-3.5 bg-white border-solid border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-indigo-400 shadow-xs"
              />
              {keyword && (
                <button
                  type="button"
                  onClick={() => setKeyword("")}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 text-sm"
                >
                  ✕
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={handleAddButton}
              style={{
                backgroundColor:
                  process.env.REACT_APP_ENTERPRISE_COLOUR_BUTTON || "#2B3688",
              }}
              className="h-[41.6px] px-3.5 rounded-lg flex items-center justify-center text-white shadow-xs active:scale-95 transition-all cursor-pointer flex-shrink-0"
              title="Add Meeting"
            >
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" />
              </svg>
            </button>
          </div>

          {/* Filters Row: Meeting Type & Outcome */}
          <div className="flex flex-row gap-2 w-full">
            <div className="flex flex-col gap-1 w-1/2">
              <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                Meeting Type
              </label>
              <select
                value={selectedFilter}
                onChange={(e) => SetSelectedFilter(e.target.value)}
                className="w-full h-[38px] px-2.5 bg-white border border-gray-200 rounded-lg text-xs sm:text-sm text-gray-800 focus:outline-none focus:ring-1 focus:ring-indigo-400 shadow-xs cursor-pointer"
              >
                {meetingTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1 w-1/2">
              <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                Outcome
              </label>
              <select
                value={selected1Filter}
                onChange={(e) => SetSelected1Filter(e.target.value)}
                className="w-full h-[38px] px-2.5 bg-white border border-gray-200 rounded-lg text-xs sm:text-sm text-gray-800 focus:outline-none focus:ring-1 focus:ring-indigo-400 shadow-xs cursor-pointer"
              >
                {outcomes.map((outcome) => (
                  <option key={outcome} value={outcome}>
                    {outcome}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Branch Filter Row */}
          {Entity?.branch && Entity.branch.length > 0 && (
            <div className="flex flex-col gap-1 w-full">
              <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                Branch
              </label>
              <select
                value={selectedBranch}
                onChange={(e) => SetSelectedBranch(e.target.value)}
                className="w-full h-[38px] px-2.5 bg-white border border-gray-200 rounded-lg text-xs sm:text-sm text-gray-800 focus:outline-none focus:ring-1 focus:ring-indigo-400 shadow-xs cursor-pointer"
              >
                <option value="ALL">ALL</option>
                {Entity.branch.map((branch) => (
                  <option key={branch.id || branch.state} value={branch.state}>
                    {branch.state}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Loading Skeletons */}
          {isLoading && (
            <div className="flex flex-col gap-3 w-full">
              <CardSkeleton />
              <CardSkeleton />
            </div>
          )}

          {/* Meeting Cards List */}
          {!isLoading && data1?.results && data1.results.length > 0 && (
            <div className="flex flex-col gap-3 w-full">
              {data1.results.map((meet, idx) => (
                <MeetingCard
                  key={meet._id || meet.id || idx}
                  data={meet}
                  onEdit={handleEditMeeting}
                  onView={handleViewMeeting}
                />
              ))}
            </div>
          )}

          {/* Empty State */}
          {!isLoading && (!data1?.results || data1.results.length === 0) && (
            <div className="flex flex-col items-center justify-center py-12 px-4 text-center bg-white rounded-xl border border-gray-100 shadow-sm">
              <div className="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center text-gray-400 mb-2">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <p className="text-sm font-semibold text-gray-800">No meetings found</p>
              <p className="text-xs text-gray-500 mt-0.5">Click + to add meeting details.</p>
            </div>
          )}

          {/* Mobile Pagination */}
          {!isLoading && totalCount > pageSize && (
            <div className="flex items-center justify-between pt-2 pb-1 text-xs text-gray-600 border-t border-gray-100">
              <button
                type="button"
                onClick={() => previousPage()}
                disabled={!canPreviousPage}
                className={`px-3 py-1.5 rounded-lg border border-gray-200 bg-white font-medium ${
                  !canPreviousPage
                    ? "opacity-40 cursor-not-allowed"
                    : "hover:bg-gray-50 active:scale-95 cursor-pointer"
                }`}
              >
                Previous
              </button>
              <span className="font-semibold text-gray-700">
                {pageIndex + 1} / {Math.max(1, totalPageCount)} ({totalCount})
              </span>
              <button
                type="button"
                onClick={() => nextPage()}
                disabled={!canNextPage}
                className={`px-3 py-1.5 rounded-lg border border-gray-200 bg-white font-medium ${
                  !canNextPage
                    ? "opacity-40 cursor-not-allowed"
                    : "hover:bg-gray-50 active:scale-95 cursor-pointer"
                }`}
              >
                Next
              </button>
            </div>
          )}
        </div>
      ) : (
        /* Desktop View (Standard Table Layout) */
        <>
          <div className="flex flex-col lg:flex-row w-full gap-3 justify-between items-stretch lg:items-end mb-3">
            <div className="w-full lg:w-auto">
              <TableFilter
                onClickSearch={onClickSearch}
                onClickFilterCallback={onClickFilterCallback}
                defaultKeyword={keyword}
                deBouncingFlag={true}
              />
            </div>
            <div className="flex flex-wrap items-end gap-2.5">
              <div>
                <label className="label mb-1 text-xs text-gray-600">Branch</label>
                <div className="select is-small">
                  <select
                    value={selectedBranch}
                    onChange={(e) => SetSelectedBranch(e.target.value)}
                  >
                    <option value="ALL">ALL</option>
                    {Entity?.branch?.map((branch) => (
                      <option key={branch.id} value={branch.state}>
                        {branch.state}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="label mb-1 text-xs text-gray-600">Meeting Type</label>
                <div className="select is-small">
                  <select
                    value={selectedFilter}
                    onChange={(e) => SetSelectedFilter(e.target.value)}
                  >
                    <option value="ALL">ALL</option>
                    <option value="In-Person">In-Person</option>
                    <option value="Video Call">Video Call</option>
                    <option value="Phone Call">Phone Call</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="label mb-1 text-xs text-gray-600">Outcome</label>
                <div className="select is-small">
                  <select
                    value={selected1Filter}
                    onChange={(e) => SetSelected1Filter(e.target.value)}
                  >
                    <option value="ALL">ALL</option>
                    <option value="Positive">Positive</option>
                    <option value="Neutral">Neutral</option>
                    <option value="Needs Follow-up">Needs Follow-up</option>
                    <option value="Pending">Pending</option>
                  </select>
                </div>
              </div>
              <div className="w-full sm:w-auto">
                <button
                  type="button"
                  className="button is-small w-full sm:w-auto font-semibold"
                  style={{
                    backgroundColor:
                      process.env.REACT_APP_ENTERPRISE_COLOUR_BUTTON,
                    color: "#fff",
                  }}
                  onClick={() => {
                    handleAddButton();
                  }}
                >
                  + Add Meeting Details
                </button>
              </div>
            </div>
          </div>
          <div
            className="box overflow-hidden p-2 sm:p-4"
            style={{ height: "calc(100vh - 230px)" }}
          >
            <div
              className="w-full overflow-x-auto overflow-y-auto"
              style={{ height: "calc(100% - 60px)" }}
            >
              <table className="table is-fullwidth is-small ">
                <thead style={{ backgroundColor: "#BCD6FF" }}>
                  {headerGroups.map((headerGroup) => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                      <th>S.No</th>
                      {headerGroup.headers.map((column) => (
                        <th
                          {...column.getHeaderProps(column.getSortByToggleProps())}
                        >
                          {column.render("Header")}
                          <Sorting column={column} />
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody
                  className="contractlist"
                  style={{ fontSize: "0.9rem", position: "relative" }}
                  {...getTableBodyProps()}
                >
                  {page.map((row, i) => {
                    prepareRow(row);
                    return (
                      <tr {...row.getRowProps()}>
                        <td>{pageIndex * pageSize + i + 1}</td>
                        {row.cells.map((cell) => {
                          let e = cell["row"]["original"];
                          switch (cell["column"]["Header"]) {
                            case "Attendees":
                              return (
                                <td {...cell.getCellProps()}>
                                  <span>{e.externalAttendees.join(",")}</span>
                                </td>
                              );
                            case "Internal Attendees":
                              return (
                                <td {...cell.getCellProps()}>
                                  <span>{e.internalAttendees.join(",")}</span>
                                </td>
                              );
                            case "Outcome":
                              return (
                                <td {...cell.getCellProps()}>
                                  <span style={{ color: getStatusColor(e.outcome) }}>
                                    {e.outcome}
                                  </span>
                                </td>
                              );
                            case "Next Follow-up":
                              return (
                                <td {...cell.getCellProps()}>
                                  <span
                                    style={{
                                      color: getFollowUpColor(e.followUpDate),
                                    }}
                                  >
                                    {e.followUpDate.length > 0
                                      ? Utils.ddmmyyyy(new Date(e.followUpDate))
                                      : ""}
                                  </span>
                                </td>
                              );
                            case "Meeting Date":
                              return (
                                <td {...cell.getCellProps()}>
                                  <a
                                    onClick={() => {
                                      ManageCRMStore.manageCRMRec.set(e);
                                      routerStore.managecrmCurrentViewNew = "view";
                                    }}
                                  >
                                    {Utils.formatDateTime(e.dateTime)}
                                  </a>
                                </td>
                              );
                            case "Actions":
                              return (
                                <td {...cell.getCellProps()}>
                                  <div className="field has-addons">
                                    <p className="control">
                                      <button
                                        className="button is-info is-inverted"
                                        onClick={() => {
                                          ManageCRMStore.manageCRMRec.set(e);
                                          routerStore.managecrmCurrentViewNew =
                                            "edit";
                                        }}
                                      >
                                        <span className="icon">
                                          <i className="fas fa-pen-to-square"></i>
                                        </span>
                                      </button>
                                    </p>
                                  </div>
                                </td>
                              );
                            default:
                              return (
                                <td {...cell.getCellProps()}>
                                  <span>{cell.render("Cell")}</span>
                                </td>
                              );
                          }
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <ReactTablePagination
                page={page}
                gotoPage={gotoPage}
                previousPage={previousPage}
                nextPage={nextPage}
                canPreviousPage={canPreviousPage}
                canNextPage={canNextPage}
                pageOptions={pageOptions}
                pageSize={pageSize}
                pageIndex={pageIndex}
                pageCount={pageCount}
                setPageSize={setPageSize}
                manualPageSize={manualPageSize}
                dataLength={totalCount}
                rows={rows}
              />
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default MeetingDetails;
